import { ButtonWithIcon, CardSkeletonLoader, Modal, ErrorBoundary } from '@/components/atoms'
import { SaveCancelFooter, SectionTitle } from '@/components/molecules'
import { PageContainer } from '@/components/organisms'
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook'
import { handleGetActivity } from '@/services/activities'
import { useAppSelector, useAppDispatch } from '@/store'
// import { WARNING_MESSAGE } from '@/types/Activity'

import {  setToastNotifications } from '@/store/slices/commonSlice'
import { getFormattedDate, getDisplayCurrency, isPublished } from '@/utils/commons'
import { COLOR } from '@/utils/constants/colors'
import { Constants, localCurrencyText } from '@/utils/constants/commonConstants'
import { MODAL_TYPE, SIZE } from '@/utils/constants/globalStyle'
import { useEffect, useCallback, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {validate} from 'uuid';
import { useErrorHandler } from '@/hooks/useErrorHandler'
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert'

import { setActivityFormData} from '@/store/slices/activitySlice'
const ActivityReviewField = ({ label, value }) => {

  let displayValue = value;
  const upperCaseLabels = ['Activity Type', 'Receive Points','Order Type', 'Activity Name']
  if ( upperCaseLabels.includes(label)) {
    displayValue = value?.toUpperCase()
  }
  if (label === 'Start Date' || label === 'End Date') {
    displayValue = getFormattedDate(value, false, '-', 'yyyy-mm-dd')
  }
  return (
    <>
      {label === 'Start Date' && <SectionTitle label={'Availability'} />}
      {label === 'Local Currency' && <SectionTitle label={'Point Allocation'} />}
      {label === 'Number Of Cases' && <SectionTitle label={'Point Allocation (Number Of Cases)'} />}
      <div className="mt-4 flex w-full border-b border-gray-700 pb-2 md:w-1/2">
        <div className="w-1/2">
          <p className="pl-1 text-sm text-[#D0D0D0]">{label === 'Receive Points' ? 'Frequency' : label}</p>
        </div>
        <div className="w-1/2">
          <p className="pl-1 font-bold text-white">{displayValue}</p>
        </div>
      </div>
    </>
  )
}

const ActivityReview = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { handleApiError } = useErrorHandler()
  const { id } = useParams(); 
  const [searchParams] = useSearchParams();
  const reviewParam = searchParams.get('review');
  const isReviewPublished = reviewParam === Constants.ReviewPublished;
  const [loading, setLoading] = useState(false);
  const {getFormDataWithOrder} = useActivityReview()

  const getActivity = useCallback(async (id) => {
    try {
      const paramKey = validate(id) ? 'uuid' : 'id';
      setLoading(true)
      dispatch(setActivityFormData({})); // Reset form data before fetching new activity
     
      const response = await handleGetActivity(`${paramKey}=${id}`, false);
      setLoading(false)
      if (response?.data.success) {
        dispatch(setToastNotifications({ show: true, message: 'Activity data fetched successfully', type: 'success' }))
        if(response?.data?.content?.length>0){
          const data = response?.data?.content[0].versionData;
          // Extract activityId from the correct location
          const activityId = data?.activityId || response?.data?.content[0]?.activityId || response?.data?.activityId || id;
          const formDataWithOrder = getFormDataWithOrder(data);
          dispatch(setActivityFormData({...formDataWithOrder, status: data.status, id: activityId}));
        }
      }else if( response?.data){
        dispatch(setToastNotifications({ show: true, message: 'Activity data fetched successfully', type: 'success' }))
        // Extract activityId from the correct location
        const activityId = response?.data?.activityId || id;
        const formDataWithOrder = getFormDataWithOrder(response?.data);
        dispatch(setActivityFormData({...formDataWithOrder, status: response?.data.status, id: activityId}));
      }
       else {
         dispatch(setToastNotifications({ show: true, message: 'Failed to fetch activity data', type: 'error' }))
        console.error('Failed to fetch activity data:', response?.message);
      }
    } catch (error) {
      setLoading(false)
      console.error('Error fetching activity data:', error);
      handleApiError(error);
    }
  },[dispatch, getFormDataWithOrder, handleApiError]);


  useEffect(()=>{
    if(id)
      getActivity(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const {
    handlePublish,
    handleDraft,
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    isCancel,
    activityFormData,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
  } = useActivityReview();

  const countryCode = useAppSelector(state => state.countryCode).countryCode
  const displayCurrency = getDisplayCurrency(countryCode)
  return (
    <ErrorBoundary>
      <>
        <PageContainer data-testid="activity-review" minHeight={'65vh'}>
        { loading && <CardSkeletonLoader count={3}/>}
        {!loading && <>
          <div data-testid="activity-review-content" className="flex items-center justify-between">
            <SectionTitle label={'Activity Setup'} />
            <ButtonWithIcon
              uiSchema={{
                className: 'ml-8 flex items-center text-sm text-[#0069D7] border-none',
                title: 'Edit',
                size: SIZE.SMALL,
                variant: COLOR.TERTIARY,
                isDark: true,
              }}
              iconLeading="edit"
              handleButton={() => navigate('/rewards/activity/add')}
            />
          </div>
          {showToast && (
            <DuplicateAlert 
              type='info' 
              message={toastMessage} 
              show={true} 
              onCloseClick={handleToastClose} 
              handleClick={handleViewActivity}
            />
          )}
          <>
            {Object.entries(activityFormData)
              .filter(([key]) => key !== 'hasEndDate' && key !== 'status' && key !== 'name' && key !== 'uuid' && key !== 'id')
              .map(([key, value]) => (
                <ActivityReviewField
                  key={key}
                  label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  value={key === localCurrencyText && value ? `${value} ${displayCurrency}` : value || ''}
                />
              ))}

            {isCancel ? (
              <Modal
                type={MODAL_TYPE.FLUID}
                showModal={isCancel}
                onCloseModal={handleCancel}
                title="Are you sure you want to cancel?"
                text={
                  'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'
                }
                showPrimaryButton={true}
                primaryButtonText="Yes"
                onClickPrimaryButton={onClickPrimaryButton}
                showSecondaryButton={true}
                secondaryButtonText="No"
                onClickSecondaryButton={onClickSecondaryButton}
                footerProps={{
                  size: 'medium',
                  type: 'primary',
                }}
                isDark={true}
              />
            ) : null}
          </>
          </>
    }
        {/* <WarningLabel title={WARNING_MESSAGE.SCHEDULING_CONFLICT_DETECTED} /> */}
        </PageContainer>
        {!isReviewPublished &&
        <SaveCancelFooter
          isReview={true}
          handleCancel={handleCancel}
          nextAction={handlePublish}
          draftAction={handleDraft}
          disableDraft={isPublished(activityFormData?.status)}
        />}
      </>
    </ErrorBoundary>
  )
}

export default ActivityReview
